import { services } from '../app';
import { constellationAt, constellationName } from '../astro/solar';
import { constellationReminder } from '../data/islam';
import { Reminder } from './Reminder';
import { starName } from '../data/catalog';
import { vecToRaDec } from '../lib/math';
import { selection } from '../state';
import { fmtMag } from './format';
import { Stat } from './chrome';
import { SimbadObject, distanceWords, typeName } from '../live/simbad';

type HeaderFn = (title: string, subtitle: string, icon: string) => preact.JSX.Element;

/** A figure of a style loaded from a file: its name, stars and whose work it is. */
export function FigureInfo({ index, header, whereRow }: { index: number; header: HeaderFn; whereRow: preact.JSX.Element | null }) {
  const s = services.value!;
  const c = s.engine.culture;
  if (!c || !c.data.figures[index]) return <div class="working"><div class="spinner" /></div>;
  const f = c.data.figures[index];
  const cat = s.engine.stars;
  const members = [...(c.geo.stars[index] ?? [])].sort((x, y) => cat.mag[x] - cat.mag[y]);
  const { ra, dec } = vecToRaDec(c.geo.anchors[index]);
  const at = constellationAt(ra, dec);
  const iau = at.name;
  // The same figure as the IAU constellation of that name ("Bootes" is Boötes), else the one it lies in.
  const conId = s.engine.cons.constellations.find((k) => k.name === f.en || (f.en === 'Bootes' && k.id === 'Boo'))?.id ?? at.id;
  const reminder = constellationReminder(conId);
  return (
    <>
      {header(f.en, `${c.data.name} · in ${iau}`, 'star')}
      {whereRow}
      {reminder && <Reminder text={reminder} />}
      {(f.native || f.pron) && (
        <div class="culture-names">
          {f.native && <span class="culture-native">{f.native}</span>}
          {f.pron && <span class="muted">{f.pron}</span>}
        </div>
      )}
      {f.text?.map((p, k) => <p class="culture-text" key={k}>{p}</p>)}
      {members.length > 0 && (
        <div class="list">
          <div class="row-label">Its stars</div>
          {members.slice(0, 8).map((i) => {
            const n = c.starNames.get(i);
            return (
              <button class="list-row btn-row" key={i} onClick={() => (selection.value = { kind: 'star', index: i })}>
                <span>
                  {starName(cat, i)}
                  {n && (n.en || n.native) && <span class="muted"> · {n.en || n.native}</span>}
                </span>
                <span class="tnum muted">mag {fmtMag(cat.mag[i])}</span>
              </button>
            );
          })}
        </div>
      )}
      <CultureCredit />
    </>
  );
}

/** The source's authors and licence, as its licence asks. */
export function CultureCredit() {
  const c = services.value!.engine.culture;
  if (!c) return null;
  const d = c.data;
  return (
    <div class="fine culture-credit">
      {d.sourceUrl ? <a href={d.sourceUrl} target="_blank" rel="noopener">{d.source}</a> : d.source}. {d.authors ? `${d.authors} ` : ''}
      Licence: <a href={d.licenceUrl} target="_blank" rel="noopener">{d.licence}</a>.{' '}
      {d.adapted}.
    </div>
  );
}

/** Something identified in SIMBAD from a tap on the sky photograph. */
export function SimbadInfo({ o, header, whereRow }: { o: SimbadObject; header: HeaderFn; whereRow: preact.JSX.Element | null }) {
  const iau = constellationName(o.ra, o.dec);
  const dist = distanceWords(o);
  return (
    <>
      {header(o.name, `${typeName(o.otype) ?? 'Object'} · in ${iau}`, o.otype.includes('G') && !o.otype.includes('*') ? 'eye' : 'star')}
      {whereRow}
      <div class="stats">
        <Stat label="Brightness" value={o.v != null ? `mag ${fmtMag(o.v)}` : 'unknown'} sub={o.v == null ? '' : o.v > 12 ? 'needs a large telescope' : o.v > 9 ? 'needs a telescope' : o.v > 6 ? 'binoculars' : 'visible by eye'} />
        <Stat label="Distance" value={dist ?? 'unknown'} sub={o.plx ? 'from its parallax' : dist ? 'from its redshift' : ''} />
      </div>
      <p class="note">Found by tapping the sky photograph. SIMBAD knows millions of objects beyond the Solar System, most far too faint to see without a telescope.</p>
      <a class="btn-ghost small" href={`https://simbad.cds.unistra.fr/simbad/sim-id?Ident=${encodeURIComponent(o.id)}`} target="_blank" rel="noopener">More about it in SIMBAD</a>
      <div class="fine">Data: SIMBAD database, CDS, Strasbourg (Wenger et al. 2000).</div>
    </>
  );
}
